import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ToastService } from '../services/toast.service';
import { WorkspaceFacade } from '../state/workspace/workspace.facade';

/** Where an authenticated user should land based on workspace setup. */
export type AuthEntryRoute = '/home' | '/create-organization' | '/create-project';

const WORKSPACE_SETUP_TOASTS = {
  organization: {
    title: 'Setup required',
    message: 'You need an organization before you can use the workspace. Create one to get started.',
  },
  project: {
    title: 'Setup required',
    message: 'Your organization needs at least one project before you can continue.',
  },
} as const;

export function resolveAuthEntryRoute(orgCount: number, projectCount: number): AuthEntryRoute {
  if (orgCount > 0 && projectCount > 0) return '/home';
  if (orgCount === 0) return '/create-organization';
  return '/create-project';
}

export function notifyWorkspaceSetupRequired(toast: ToastService, route: AuthEntryRoute): void {
  if (route === '/create-organization') {
    toast.info(WORKSPACE_SETUP_TOASTS.organization.message, WORKSPACE_SETUP_TOASTS.organization.title);
  } else if (route === '/create-project') {
    toast.info(WORKSPACE_SETUP_TOASTS.project.message, WORKSPACE_SETUP_TOASTS.project.title);
  }
}

export async function navigateAfterAuthentication(
  router: Router,
  workspace: WorkspaceFacade,
  toast: ToastService
): Promise<void> {
  workspace.reset();
  await workspace.refreshWorkspaceData();

  const error = await firstValueFrom(workspace.error$);
  if (error) {
    await router.navigate(['/home']);
    return;
  }

  const organizations = await firstValueFrom(workspace.organizations$);
  const projects = await firstValueFrom(workspace.projects$);
  const route = resolveAuthEntryRoute(organizations.length, projects.length);
  notifyWorkspaceSetupRequired(toast, route);
  await router.navigate([route]);
}
